import { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { resetPasswordFormSchema } from 'utils/form-validation-schema';
import { InputControlled } from 'components/UI/InputControlled/InputControlled';
import { resetUserPassword } from 'services/UserAccountService';
import { ReactComponent as HelpIcon } from 'assets/icons/svgs/icon-help.svg';
import useWindowDimensions from 'hooks/use-window-dimensions';
import { Checkbox } from 'components/UI/Checkbox/Checkbox';
import { Callout } from 'components/UI/Callout/Callout';
import './ResetPasswordForm.scss';

export const ResetPasswordForm = (props) => {
  const { IsTokenValid, EmailAddress, ErrorMessage, SuccessMessage } = props;

  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(SuccessMessage || '');
  const [errorMessage, setErrorMessage] = useState(ErrorMessage || '');
  const [tokenIsValid, setTokenIsvalid] = useState(IsTokenValid);
  const [openPopup, setOpenPopup] = useState(false);
  const { isMobile } = useWindowDimensions();
  const popupHintClass = isMobile ? 'hint-visible-m' : 'hint-visible-d';
  const [newPasswordInputType, setNewPasswordInputType] = useState('password');
  const showPasswordChangeHandler = (event) => {
    const checkboxIsChecked = event.target.checked;
    const type = checkboxIsChecked ? 'text' : 'password';
    setNewPasswordInputType(type);
  };

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(resetPasswordFormSchema),
    shouldFocusError: false,
  });

  const onSubmitHandler = async (formData) => {
    const requestPayload = {
      EmailAddress,
      Message: null,
      IsLoading: true,
      IsTokenValid: true,
      IsTokenExpired: false,
      Redirect: false,
      RedirectUrl: null,
      ErrorMessage: '',
      ComplexErrorMessage: null,
      SuccessMessage: null,
      newPassword: formData.newInputPassword,
      showPassword: true,
    };

    setIsProcessing(true);

    const response = await resetUserPassword(requestPayload);

    if (response.Redirect) {
      window.location.href = response.RedirectUrl;
    } else if (!response.IsTokenValid) {
      setTokenIsvalid(response.IsTokenValid);
    } else if (response.SuccessMessage) {
      setSuccessMessage(response.SuccessMessage);
    } else if (response.ErrorMessage) {
      setErrorMessage(response.ErrorMessage);
    }

    setIsProcessing(false);
  };
  const showPopupHandler = (inputId) => {
    if (!isMobile) {
      setOpenPopup(inputId);
    }
  };
  const showPopupMobileHandler = (inputId) => {
    setOpenPopup((prevState) => (prevState ? false : inputId));
  };
  const renderPasswordHintButton = (inputId) => (
    <button
      type="button"
      className="hint-btn visible-xs"
      onClick={() => showPopupMobileHandler(inputId)}
    >
      <HelpIcon className="registration-form__help-icon" />
    </button>
  );

  return (
    <section className="container">
      <FormProvider {...methods}>
        <form
          name="resetPasswordForm"
          className="reset-password-form"
          noValidate="novalidate"
          onSubmit={methods.handleSubmit(onSubmitHandler)}
        >
          {successMessage && (
            <>
              <div className="row">
                <div className="col-md-8 col-md-offset-1">
                  <h2>Password successfully changed!</h2>
                </div>
              </div>
              <div className="row">
                <div className="col-md-8 col-md-offset-1">
                  <p className="large">Your account password has been successfully reset.</p>
                  <p className="large">
                    Visit our <a href="/whats-on">What's on page</a> to browse the latest events or
                    return to the <a href="/cart">Cart</a> to continue shopping.If you were in the
                    process of making a donation, <a href="/donate?resetpassword=1">click here</a>{' '}
                    to return to your donation.
                  </p>

                  <Callout successMessage={successMessage} />
                </div>
              </div>
            </>
          )}
          {tokenIsValid && !successMessage && (
            <div className="row">
              <div className="col-md-8 col-md-offset-1">
                <h2>Reset your password</h2>
                <p className="large form-intro">
                  You are seeing this message because you requested a reminder of your log in
                  details using the forgotten password form. Please reset your password to access
                  your account again.
                </p>
                <p>
                  Email address:
                  <big>
                    <strong>{EmailAddress}</strong>
                  </big>
                </p>
              </div>

              <div className="col-sm-6 col-md-4 col-md-offset-1">
                <div className="form-group">
                  <div className="input-container has-hint">
                    <InputControlled
                      type={newPasswordInputType}
                      id="newInputPassword"
                      labelText="New password"
                      showPopupHandler={showPopupHandler}
                      onSubmit={methods.handleSubmit(onSubmitHandler)}
                      onChange={(e) => setPassword(e.target.value)}
                    />

                    <div
                      className={`form-hint ${
                        openPopup === 'newInputPassword' ? popupHintClass : ''
                      }`}
                    >
                      <p>
                        Password should be <strong>6 or more characters</strong> and contain:
                      </p>
                      <p>
                        - English upper OR lower case letters (A to Z); <strong>and</strong>
                      </p>
                      <p>- At least one number (0 to 9) or character (such as !, $, #, %)</p>
                      <p>
                        <strong>But please don't:</strong>
                      </p>
                      <p>- Use your account login details or name in the password</p>
                    </div>

                    {renderPasswordHintButton('newInputPassword')}
                  </div>
                </div>

                {/* end of input */}

                <div className="form-group">
                  <div className="input-container ">
                    <Checkbox
                      checkboxChangeHandler={showPasswordChangeHandler}
                      label="Show password"
                      muiProps={{ id: 'showPassword' }}
                    />
                  </div>
                </div>
                {/* end of checkbox */}

                <button type="submit" className="btn btn-primary" data-testid="sign-in-button">
                  {isProcessing ? 'Processing...' : 'Submit'}
                </button>
              </div>
            </div>
          )}
          {errorMessage && (
            <div className="row">
              <div className="col-md-8 col-md-offset-1">
                <div className="callout-row">
                  <Callout errorMessage={errorMessage} />
                </div>
              </div>
            </div>
          )}
          {methods.formState.errors.newInputPassword && !errorMessage && (
            <div className="row">
              <div className="col-md-8 col-md-offset-1">
                <Callout errorMessage="Please complete reset password form" />
              </div>
            </div>
          )}
          {/* end of button */}
          {!tokenIsValid && !successMessage && (
            <>
              <div className="row">
                <div className="col-md-8 col-md-offset-1">
                  <h2>Reset your password</h2>
                </div>
              </div>
              <div className="row">
                <div className="col-md-8 col-md-offset-1">
                  <div className="callout-row">
                    <div className="callout-container callout-error">
                      <p>
                        The password reset token appears to be expired or incorrect. Please re-try
                        to &ensp;
                        <a href="/my-account/forgot-password">reset your password</a>
                        &ensp; and follow the instructions in the email.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </form>
      </FormProvider>
    </section>
  );
};

export const ResetPasswordFormSC = (props) => {
  const { sitecoreData } = props;
  return <ResetPasswordForm {...sitecoreData} />;
};

ResetPasswordFormSC.propTypes = {
  sitecoreData: PropTypes.object,
};

ResetPasswordForm.propTypes = {
  IsTokenValid: PropTypes.bool,
  EmailAddress: PropTypes.string,
  ErrorMessage: PropTypes.string,
  SuccessMessage: PropTypes.string,
};
